import React, { useState, useEffect } from 'react';
import {
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    CircularProgress,
    Grid,
    Alert
} from '@mui/material';

interface ConsultaChatProps {
    onConsulta: (consulta: any) => Promise<string>;
}

const ConsultaChat: React.FC<ConsultaChatProps> = ({ onConsulta }) => {
    const [pregunta, setPregunta] = useState('');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [nroCliente, setNroCliente] = useState('');
    const [nroVendedor, setNroVendedor] = useState('');
    const [asentado, setAsentado] = useState('');
    const [loading, setLoading] = useState(false);
    const [respuesta, setRespuesta] = useState('');
    const [errorFechas, setErrorFechas] = useState('');

    useEffect(() => {
        // Obtener la fecha actual
        const hoy = new Date();
        
        // Calcular la fecha de hace 3 meses
        const tresMesesAtras = new Date();
        tresMesesAtras.setMonth(hoy.getMonth() - 3);
        
        // Formatear las fechas al formato YYYY-MM-DD
        const formatearFecha = (fecha: Date) => {
            return fecha.toISOString().split('T')[0];
        };
        
        setFechaDesde(formatearFecha(tresMesesAtras));
        setFechaHasta(formatearFecha(hoy));
    }, []);

    const validarRangoFechas = (desde: string, hasta: string): boolean => {
        if (!desde || !hasta) return true;
        
        const fechaInicio = new Date(desde);
        const fechaFin = new Date(hasta);
        
        // Calcular la diferencia en meses
        const diffMeses = (fechaFin.getFullYear() - fechaInicio.getFullYear()) * 12 + 
                         (fechaFin.getMonth() - fechaInicio.getMonth());
        
        if (diffMeses > 3) {
            setErrorFechas('El rango de fechas no puede ser mayor a 3 meses');
            return false;
        }
        
        setErrorFechas('');
        return true;
    };

    const handleFechaDesdeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nuevaFecha = e.target.value;
        setFechaDesde(nuevaFecha);
        validarRangoFechas(nuevaFecha, fechaHasta);
    };

    const handleFechaHastaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nuevaFecha = e.target.value;
        setFechaHasta(nuevaFecha);
        validarRangoFechas(fechaDesde, nuevaFecha);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validarRangoFechas(fechaDesde, fechaHasta)) {
            return;
        }

        setLoading(true);
        try {
            const consulta = {
                pregunta,
                fechaDesde,
                fechaHasta,
                nroCliente,
                nroVendedor,
                asentado
            };
            const respuesta = await onConsulta(consulta);
            setRespuesta(respuesta);
        } catch (error) {
            console.error('Error al procesar la consulta:', error);
            setRespuesta('Error al procesar la consulta. Por favor, intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 3 }}>
            <Typography 
                variant="h4" 
                gutterBottom
                sx={{
                    fontWeight: 600,
                    color: '#1976d2',
                    mb: 3
                }}
            >
                Consulta Inteligente
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0', p: 3 }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Pregunta"
                                        multiline
                                        rows={4}
                                        value={pregunta}
                                        onChange={(e) => setPregunta(e.target.value)}
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                {errorFechas && (
                                    <Grid item xs={12}>
                                        <Alert severity="error" sx={{ borderRadius: 2 }}>{errorFechas}</Alert>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fecha Desde"
                                        type="date"
                                        value={fechaDesde}
                                        onChange={handleFechaDesdeChange}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fecha Hasta"
                                        type="date"
                                        value={fechaHasta}
                                        onChange={handleFechaHastaChange}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Nro. Cliente"
                                        value={nroCliente}
                                        onChange={(e) => setNroCliente(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Nro. Vendedor"
                                        value={nroVendedor}
                                        onChange={(e) => setNroVendedor(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Asentado"
                                        value={asentado}
                                        onChange={(e) => setAsentado(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={loading || !pregunta || !!errorFechas}
                                        sx={{
                                            borderRadius: 2,
                                            px: 4,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Consultar'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                            <Typography 
                                variant="h6" 
                                gutterBottom
                                sx={{
                                    fontWeight: 600,
                                    color: '#1976d2',
                                    mb: 2
                                }}
                            >
                                Respuesta
                            </Typography>
                            {respuesta ? (
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        whiteSpace: 'pre-wrap',
                                        bgcolor: '#fafafa',
                                        p: 2,
                                        borderRadius: 2,
                                        minHeight: 200
                                    }}
                                >
                                    {respuesta}
                                </Typography>
                            ) : (
                                <Typography 
                                    variant="body2" 
                                    color="textSecondary"
                                    sx={{
                                        bgcolor: '#fafafa',
                                        p: 2,
                                        borderRadius: 2,
                                        minHeight: 200,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    Ingrese una pregunta para obtener una respuesta
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Card>
    );
};

export default ConsultaChat; 